// Aufgeteilt aus dem früheren 2600-Zeilen-Monolithen `musicXmlDom.ts` (Import-Pfad unverändert:
// `@/composables/musicXmlDom` zeigt auf `index.ts`). Abhängigkeitsregel im Ordner: geteilte
// Basis-Helfer liegen in `core.ts`; gerichtete Geschwister-Imports sind erlaubt, ZYKLEN nicht
// (aktuell: bass→chordInference, notes→core, diatonic→notes, structure→notes,
// lyrics→bass, clipboard→structure, fragment→structure).
import {type ClipboardNote, type NoteInfo, type Pitch, buildNoteElement, chordGroupOf, findPartAndMeasureNote, movePrimaryChildren, noteDurationTicks, noteKey, parseMusicXml, pitchOfNote, pitchToSemitone, samePitch, serializeMusicXml, voiceOf} from './core';
import {removeOutgoingBows} from './notes';

// ============ Diatonik (Auswahl-Variante der diatonischen Verschiebung) ============
//
// Clientseitiges Pendant zu `griffschrift/transpose/diatonic.py` für die **Notenauswahl** (das
// Ganz-Score-Pendant bleibt `POST /api/diatonic-shift`). Gleiche Semantik: Intervall → Stufen
// (`sign·(|n|−1)`), nicht-diatonische Quellnoten werden übersprungen, Dedup nach Schreibweise
// (`step/alter/octave`, wie music21 `nameWithOctave`), Dreiklang-Reduktion nur bei genau 3 Tönen.
// Bewusste Abweichung: die Oktave folgt dem Buchstabenwechsel B→C (musikalisch korrekt; das Backend
// rechnet `octave_shift` relativ zur Tonika und liefert z. B. in G-Dur für B4 + Terz ein D4).

const LETTERS = ['C', 'D', 'E', 'F', 'G', 'A', 'B'] as const;
export type Letter = typeof LETTERS[number];
/** Buchstabe → Alteration der geltenden Tonart (aus `<fifths>`). */
export type KeyAlterMap = Record<Letter, -1 | 0 | 1>;
const SHARP_ORDER: readonly Letter[] = ['F', 'C', 'G', 'D', 'A', 'E', 'B'];
const FLAT_ORDER: readonly Letter[] = ['B', 'E', 'A', 'D', 'G', 'C', 'F'];



/** Position eines eingefügten (`delta: 1`) bzw. entfernten (`delta: -1`) Akkordtons in der
 *  XML-Stimmen-Zählung (`voiceNoteIndex`), in **Anwendungsreihenfolge** – Basis für das Remap der
 *  Auswahl (`remapSelectionAfterIndexEdits`). `noteIndex` (OSMDs flache Zählung) wird bewusst nicht
 *  mitgeführt – er wird nach dem Re-Render neu abgeleitet. */
export interface IndexEdit {
    staffIndex: number;
    measureIndex?: number;
    measureNumber: number;
    voice: number;
    voiceNoteIndex: number;
    delta: 1 | -1;
}

export interface DiatonicEditResult {
    xml: string;
    edits: IndexEdit[];
}

/** String-identisch mit dem generierten `TriadReductionEnum` (hier bewusst ohne `@/generated`-Import,
 *  damit dieses Modul API-/framework-frei bleibt). */
export type TriadReductionMode = 'low_mid' | 'low_high' | 'mid_high';

/** Welcher äußere Akkordton entfernt wird (`remove_highest_note`/`remove_lowest_note` der Engine). */
export type ChordToneExtreme = 'highest' | 'lowest';

/** Vorzeichen-Zuordnung der Tonart: `fifths > 0` → Kreuze in der Reihe F C G D A E B,
 *  `fifths < 0` → Bs in der Reihe B E A D G C F. Modus ist irrelevant (Dur und paralleles Moll
 *  teilen die Vorzeichen). */
export function keyAlterMap(fifths: number): KeyAlterMap {
    const map: KeyAlterMap = {C: 0, D: 0, E: 0, F: 0, G: 0, A: 0, B: 0};
    const n = Math.max(-7, Math.min(7, Math.trunc(fifths) || 0));
    if (n > 0) for (let i = 0; i < n; i++) map[SHARP_ORDER[i]!] = 1;
    else for (let i = 0; i < -n; i++) map[FLAT_ORDER[i]!] = -1;
    return map;
}

/** Geltende `<key><fifths>` am Takt: letzte `<attributes><key>` bis einschließlich `measure`
 *  (Rückwärts-Walk über die Geschwister-Takte desselben Parts; ohne → 0 = C-Dur). */
export function fifthsInEffect(measure: Element): number {
    let m: Element | null = measure;
    while (m) {
        if (m.tagName === 'measure') {
            const f = m.querySelector('attributes > key > fifths');
            if (f) return parseInt(f.textContent || '0', 10) || 0;
        }
        m = m.previousElementSibling;
    }
    return 0;
}

/** Generisches diatonisches Intervall (2 = Sekunde, 3 = Terz, … 8 = Oktave, negativ = abwärts) →
 *  Stufenschritte (`sign · (|n| − 1)`), wie `diatonic_interval_to_shift_steps` im Backend. */
export function diatonicIntervalToSteps(interval: number): number {
    const mag = Math.abs(interval);
    if (mag < 2) return 0;
    return Math.sign(interval) * (mag - 1);
}

/** Verschiebt eine Tonhöhe um `steps` Stufen innerhalb der Tonart `alters`: reine Buchstaben-
 *  Arithmetik (C..B), Oktave wickelt beim Überlauf; die Alteration des Zielbuchstabens kommt aus
 *  der Tonart. `null`, wenn die Quellnote **nicht diatonisch** ist (Alteration ≠ Tonart-Alteration
 *  ihres Buchstabens) – analog `getScaleDegreeFromPitch → None` im Backend. */
export function diatonicShiftPitch(p: Pitch, steps: number, alters: KeyAlterMap): Pitch | null {
    const idx = LETTERS.indexOf(p.step as Letter);
    if (idx < 0) return null;
    if (alters[p.step as Letter] !== p.alter) return null;
    const raw = idx + steps;
    const step = LETTERS[((raw % 7) + 7) % 7]!;
    return {step, alter: alters[step], octave: p.octave + Math.floor(raw / 7)};
}


function semitoneOfNote(noteEl: Element): number {
    const p = pitchOfNote(noteEl);
    return p ? pitchToSemitone(p.step, p.alter, p.octave) : Number.NEGATIVE_INFINITY;
}


function measureIdentityOf(info: NoteInfo): string {
    return info.measureIndex === undefined ? `number:${info.measureNumber}` : `index:${info.measureIndex}`;
}

function indexEditFor(info: NoteInfo, voiceNoteIndex: number, delta: 1 | -1): IndexEdit {
    return {
        staffIndex: info.staffIndex,
        ...(info.measureIndex === undefined ? {} : {measureIndex: info.measureIndex}),
        measureNumber: info.measureNumber,
        voice: info.voice,
        voiceNoteIndex,
        delta,
    };
}

/** Fügt `newNote` (ohne `<chord/>`) **aufsteigend nach Tonhöhe** in die Akkord-Gruppe `group` ein
 *  und liefert die Position innerhalb der Gruppe. OSMD sortiert die Töne einer Zählzeit beim
 *  Rendern (`sortForVexflow`), die Frontend-Zählung `voiceNoteIndex` läuft also in Tonhöhen-
 *  Reihenfolge – die XML-Reihenfolge muss ihr entsprechen (music21 exportiert Akkorde ohnehin
 *  sortiert). Wird der neue Ton der tiefste, wird er Primärnote: die alte Primärnote bekommt
 *  `<chord/>` und gibt `<beam>`/`<lyric>` ab. */
function insertChordToneAscending(doc: Document, group: Element[], newNote: Element): number {
    const newSemi = semitoneOfNote(newNote);
    const target = group.find(m => semitoneOfNote(m) > newSemi);
    const primary = group[0]!;
    if (!target) {
        group[group.length - 1]!.after(newNote);
        newNote.insertBefore(doc.createElement('chord'), newNote.firstChild);
        return group.length;
    }
    if (target === primary) {
        primary.before(newNote);
        primary.insertBefore(doc.createElement('chord'), primary.firstChild);
        movePrimaryChildren(primary, newNote);
        return 0;
    }
    target.before(newNote);
    newNote.insertBefore(doc.createElement('chord'), newNote.firstChild);
    return group.indexOf(target);
}

/** Baut den Akkord-Begleiter zu `source` mit Tonhöhe `pitch`: gleiche Dauer/Typ/Punkte/Stimme,
 *  `<staff>` geerbt (Grand-Staff-Invariante), `<time-modification>` kopiert (Triolen); **ohne**
 *  Farbe, Beams, Bögen, Lyrics, Notehead, Accidental (der Ton liegt in der Tonart). */
function buildChordCompanion(doc: Document, source: Element, pitch: Pitch): Element {
    const voice = voiceOf(source);
    const staff = source.querySelector('staff')?.textContent ?? null;
    const payload: ClipboardNote = {
        isRest: false, step: pitch.step, alter: pitch.alter, octave: pitch.octave,
        type: source.querySelector('type')?.textContent || '',
        dots: source.querySelectorAll('dot').length,
        durationTicks: noteDurationTicks(source), sourceDivisions: 0,
    };
    const note = buildNoteElement(doc, payload, payload.durationTicks, payload.type, payload.dots, voice, staff);
    const tm = source.querySelector(':scope > time-modification');
    if (tm) {
        const staffEl = note.querySelector(':scope > staff');
        if (staffEl) note.insertBefore(tm.cloneNode(true), staffEl);
        else note.appendChild(tm.cloneNode(true));
    }
    return note;
}

/** Gruppiert Infos nach Notenzeile+Takt+Stimme und liefert sie **absteigend nach `voiceNoteIndex`**
 *  – so bleibt die Trefferlogik (`voiceNotes[voiceNoteIndex]`) für die noch offenen Infos gültig,
 *  obwohl jede Mutation die Indizes dahinter verschiebt. Duplikate (per `noteKey`) entfallen. */
function infosInApplicationOrder(infos: readonly NoteInfo[]): NoteInfo[] {
    const seen = new Set<string>();
    const out: NoteInfo[] = [];
    for (const i of infos) {
        const k = noteKey(i);
        if (seen.has(k)) continue;
        seen.add(k);
        out.push(i);
    }
    return out.sort((a, b) => {
        const la = `${a.staffIndex}|${measureIdentityOf(a)}|${a.voice}`;
        const lb = `${b.staffIndex}|${measureIdentityOf(b)}|${b.voice}`;
        return la === lb ? b.voiceNoteIndex - a.voiceNoteIndex : la.localeCompare(lb);
    });
}

/**
 * „Diatonisch hinzufügen" (Auswahl-Variante von `add_shifted_notes`): fügt zu jeder ausgewählten
 * Note den um `interval` (±2/3/5/6/8) diatonisch verschobenen Ton als **Akkord-Begleiter** hinzu
 * (gleiche Dauer → Taktlänge bleibt trivial erhalten). Tonart = geltende `<fifths>` des Parts am
 * Takt. Übersprungen: Pausen, Vorschläge, nicht-diatonische Quellnoten, bereits in der Gruppe
 * vorhandene Töne. Liefert das MusicXML plus die Index-Verschiebungen (`edits`, Anwendungsreihenfolge)
 * oder `null`, wenn nichts geändert wurde / bei Fehler.
 */
export function addDiatonicChordTonesInMusicXml(
    musicXml: string, infos: readonly NoteInfo[], interval: number,
): DiatonicEditResult | null {
    try {
        const steps = diatonicIntervalToSteps(interval);
        if (!steps) return null;
        const doc = parseMusicXml(musicXml);
        const edits: IndexEdit[] = [];
        for (const info of infosInApplicationOrder(infos)) {
            if (info.isRest) continue;
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            const {measure, note, voiceNotes} = found;
            if (note.querySelector('grace') || note.querySelector('rest')) continue;
            const source = pitchOfNote(note);
            if (!source) continue;
            const target = diatonicShiftPitch(source, steps, keyAlterMap(fifthsInEffect(measure)));
            if (!target) continue;
            const group = chordGroupOf(note);
            if (group.some(m => {
                const p = pitchOfNote(m);
                return !!p && samePitch(p, target);
            })) continue;
            const primaryIdx = voiceNotes.indexOf(group[0]!);
            const companion = buildChordCompanion(doc, note, target);
            const pos = insertChordToneAscending(doc, group, companion);
            edits.push(indexEditFor(info, primaryIdx + pos, 1));
        }
        if (!edits.length) return null;
        return {xml: serializeMusicXml(doc), edits};
    } catch (e) {
        console.error('Error adding diatonic chord tones in MusicXML:', e);
        return null;
    }
}

/**
 * Gemeinsamer Kern der Akkordton-Entfernung: läuft die Auswahl in Anwendungsreihenfolge ab und
 * fragt `pickRemoved` je Akkord-Gruppe (aufsteigend nach Tonhöhe sortiert) nach dem zu
 * entfernenden Ton – `null` überspringt die Gruppe.
 *
 * Mehrere selektierte Töne derselben Gruppe = **eine** Operation (die ganze Gruppe wird
 * vorgemerkt). Fällt die Primärnote weg, rückt der
 * nächste Begleiter nach (verliert `<chord/>`, erbt `<beam>`/`<lyric>`) – sonst verlöre die Stimme
 * die Dauer der Zählzeit (`MeterException`). Ausgehende Bögen des entfernten Tons werden samt
 * Gegenstück gelöst. `null`, wenn nichts geändert wurde / bei Fehler.
 */
function removeChordTonesInMusicXml(
    musicXml: string, infos: readonly NoteInfo[],
    pickRemoved: (sortedGroup: Element[]) => Element | null,
    errorLabel: string,
): DiatonicEditResult | null {
    try {
        const doc = parseMusicXml(musicXml);
        const edits: IndexEdit[] = [];
        const done = new Set<Element>();
        for (const info of infosInApplicationOrder(infos)) {
            if (info.isRest) continue;
            const found = findPartAndMeasureNote(doc, info);
            if (!found) continue;
            const {part, note, voiceNotes} = found;
            const group = chordGroupOf(note);
            const primary = group[0]!;
            if (done.has(primary)) continue;
            // Die GANZE Gruppe vormerken, nicht nur die Primärnote: fällt sie weg, rückt ein
            // Begleiter nach und wäre als neue Primärnote sonst ein zweites Mal dran (mehrere
            // selektierte Töne derselben Gruppe sind EINE Operation).
            for (const member of group) done.add(member);
            const removed = pickRemoved([...group].sort((a, b) => semitoneOfNote(a) - semitoneOfNote(b)));
            if (!removed) continue;
            const pos = voiceNotes.indexOf(removed);
            if (removed === primary) {
                const next = group[1]!;
                next.querySelector(':scope > chord')?.remove();
                movePrimaryChildren(primary, next);
            }
            removeOutgoingBows(part, removed, info.voice);
            removed.remove();
            edits.push(indexEditFor(info, pos, -1));
        }
        if (!edits.length) return null;
        return {xml: serializeMusicXml(doc), edits};
    } catch (e) {
        console.error(errorLabel, e);
        return null;
    }
}

/**
 * „Dreiklang reduzieren" (Auswahl-Variante von `reduce_triad_to_dyad`): entfernt aus jeder
 * ausgewählten Akkord-Gruppe mit **genau drei** Tönen den nicht genannten Ton (`low_mid` → höchster
 * weg, `low_high` → mittlerer weg, `mid_high` → tiefster weg); Zweiklänge/Vierklänge bleiben
 * unberührt.
 */
export function reduceTriadsInMusicXml(
    musicXml: string, infos: readonly NoteInfo[], mode: TriadReductionMode,
): DiatonicEditResult | null {
    return removeChordTonesInMusicXml(musicXml, infos, sorted => {
        if (sorted.length !== 3) return null;
        return mode === 'low_mid' ? sorted[2]! : mode === 'mid_high' ? sorted[0]! : sorted[1]!;
    }, 'Error reducing triads in MusicXML:');
}

/**
 * „Höchste/Niedrigste Note entfernen" (Auswahl-Variante von `remove_highest_note`/
 * `remove_lowest_note`): entfernt aus jeder ausgewählten Akkord-Gruppe den höchsten bzw. tiefsten
 * Ton – anders als `reduceTriadsInMusicXml` **größenunabhängig** (Zweiklang → Einzelton,
 * Vierklang → Dreiklang). Einzelne Noten bleiben unberührt.
 */
export function removeExtremeChordTonesInMusicXml(
    musicXml: string, infos: readonly NoteInfo[], which: ChordToneExtreme,
): DiatonicEditResult | null {
    return removeChordTonesInMusicXml(musicXml, infos, sorted => {
        if (sorted.length < 2) return null;
        return which === 'highest' ? sorted[sorted.length - 1]! : sorted[0]!;
    }, 'Error removing extreme chord tones in MusicXML:');
}

